import { useEffect, useRef, useId } from 'react';

// Cloudflare Turnstile challenge widget (ROADMAP.md §3.1's CAPTCHA
// closeout - bot_challenge.py's only wired-in provider). Renders nothing
// when siteKey is empty, the same "no provider configured" fail-open shape
// bot_challenge.py itself takes - a deployment that hasn't set
// BOT_CHALLENGE_PROVIDER/TURNSTILE_SITE_KEY yet shows no widget at all
// rather than a broken one, and /api/auth/signup's own verify() call
// still passes with no token in that case.
//
// Loads Cloudflare's script tag once per page (shared across any number of
// widget instances) and renders via its explicit-render API rather than
// the data-attribute auto-render mode, so onToken fires straight into
// React state instead of a global callback name per instance.
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
let scriptLoadPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
      if (existing) { existing.addEventListener('load', () => resolve()); return; }
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Turnstile script'));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

export function TurnstileWidget({ siteKey, onToken }) {
  const containerId = useId().replace(/:/g, '');
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(`#${containerId}`, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    }).catch(() => {}); // best-effort - a network/CDN failure just leaves the widget unrendered
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current);
    };
  }, [siteKey, containerId, onToken]);

  if (!siteKey) return null;
  return <div id={containerId} className="mt-4" />;
}
